// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { registerComponentEventHandlers } = require('client-v2/common/components/utils')
const AppNav = require('client-v2/common/containers/app-nav/component-smart')
const Icon = require('client-v2/common/containers/icons/component')
const SearchForm = require('client-v2/common/containers/search-form/component-smart')
const { ModalDepthScrollbarOffsetEffect } = require('client-v2/common/contexts/modal-depth')
const tag = require('client-v2/common/utils/hyperscript')
const { mediaQueries } = require('client-v2/common/utils/responsive')

const SCROLL_THROTTLE_FREQUENCY = 100 // ms (~6 frames)

const bem = BemEntity('AppHeader')

class AppHeader extends React.PureComponent {
  constructor(props) {
    super(props)
    this.blockRef = React.createRef()
    this.state = {
      isScrolledDown: false,
      isScrolledHidden: false,
      isScrollingUp: false,
      scrollY: 0
    }
    // Bind event listeners to the component context
    registerComponentEventHandlers(this)
    this.onMediaQueryChange = this.onMediaQueryChange.bind(this)
    // Throttle scroll listener to improve performance
    this.onScroll = _.throttle(this.onScroll.bind(this), SCROLL_THROTTLE_FREQUENCY)
  }

  // Component lifecycle

  componentDidMount() {
    if (typeof window === 'object') {
      if (typeof matchMedia === 'function') {
        this.mediaQuery = matchMedia(mediaQueries['small-and-down'])
        this.mediaQuery.addListener(this.onMediaQueryChange)
      }
      window.addEventListener('scroll', this.onScroll)
      this.updateScrollState()
    }
  }

  componentWillUnmount() {
    if (typeof window === 'object') {
      window.removeEventListener('scroll', this.onScroll)
      if (this.mediaQuery) {
        this.mediaQuery.removeListener(this.onMediaQueryChange)
        delete this.mediaQuery
      }
    }
  }

  // Scroll handling

  onMediaQueryChange() {
    this.updateScrollState()
  }

  onScroll() {
    const scrollY = typeof window === 'object' ? window.pageYOffset : 0
    if (scrollY !== this.state.scrollY) {
      const isScrollingUp = scrollY < this.state.scrollY
      this.updateScrollState({ isScrollingUp, scrollY })
    }
  }

  updateScrollState({ isScrollingUp, scrollY } = this.state) {
    const blockEl = this.blockRef.current
    if (blockEl) {
      const { height } = blockEl.getBoundingClientRect()
      const isSmallMedia = this.mediaQuery && this.mediaQuery.matches
      const isScrolledDown = scrollY > height
      const isScrolledHidden = isSmallMedia && isScrolledDown && !isScrollingUp
      this.setState({ isScrolledHidden, isScrolledDown, isScrollingUp, scrollY })
    }
  }

  // Render methods

  renderAuth() {
    if (this.props.user) {
      return null
    }
    return (
      tag('div', {
        className: bem.element('auth-container')
      },
        tag('button', {
          className: bem.element('auth-button', 'signup'),
          onClick: this.onSignupClick
        },
          `Join Mix`
        ),
        tag('button', {
          className: bem.element('auth-button', 'login'),
          onClick: this.onLoginClick
        },
          `Sign in`
        )
      )
    )
  }

  renderButtons() {
    const { user } = this.props
    return (
      tag('div', {
        className: bem.element('button-container')
      },
        tag(CircleButton, {
          className: bem.element('button', 'search'),
          onClick: this.onSearchClick
        },
          tag(Icon, { id: 'search' })
        ),
        !!user && tag([
          this.renderSharedButton(),
          tag(UserAvatar, {
            className: bem.element('button', 'profile'),
            user,
            badge: false,
            href: true,
            visible: true,
            onClick: this.onProfileClick
          }),
          tag('button', {
            className: bem.element('button', 'add'),
            onClick: this.onAddClick
          })
        ])
      )
    )
  }

  renderSharedButton() {
    let { sharesUnreadCount } = this.props.user
    if (sharesUnreadCount > 9) {
      sharesUnreadCount = '9+'
    }
    return (
      tag(CircleButton, {
        className: bem.element('button', 'shared'),
        onClick: this.onSharedClick
      },
        tag(Icon, { id: 'bell' }),
        !!sharesUnreadCount && (
          tag('div', {
            className: bem.element('shared-count')
          },
            sharesUnreadCount
          )
        )
      )
    )
  }

  renderLogo() {
    return (
      tag('a', {
        className: bem.element('logo-container'),
        href: '/'
      },
        tag('span', {
          className: bem.element('logo-icon')
        }),
        tag('span', {
          className: bem.element('logo-text')
        },
          tag(Icon, {
            id: 'mix-logotype'
          })
        )
      )
    )
  }

  renderNav() {
    return (
      tag(AppNav, {
        className: bem.element('nav')
      })
    )
  }

  renderSearch() {
    return (
      tag('div', {
        className: bem.element('search-container')
      },
        tag(SearchForm, {
          className: bem.element('search-form')
        })
      )
    )
  }

  render() {
    const { props, state } = this
    if (props.hidden) {
      return null
    }
    return (
      tag('div', {
        ref: this.blockRef,
        className: classNames(
          props.className,
          bem.block({
            'logged': props.user ? 'in' : 'out',
            'scrolled': state.isScrolledDown ? 'down' : 'up',
            'scrolled-hidden': state.isScrolledHidden,
            'searching': props.isSearchVisible
          })
        )
      },
        tag(ModalDepthScrollbarOffsetEffect, {
          forwardedRef: this.blockRef
        }),
        tag('div', {
          className: bem.element('leading-container')
        },
          this.renderLogo(),
          this.renderSearch(),
          this.renderNav(),
          this.renderButtons()
        ),
        this.renderAuth()
      )
    )
  }
}

// Event Handler Callbacks
AppHeader.eventHandlerKeys = [
  'onAddClick',
  'onLoginClick',
  'onProfileClick',
  'onSearchClick',
  'onSharedClick',
  'onSignupClick'
]

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')
  const SelfUserRecord = require('client-v2/common/state/user/record')

  AppHeader.propTypes = {
    // Values
    className: PropTypes.string,
    hidden: PropTypes.bool,
    isSearchVisible: PropTypes.bool,
    user: PropTypes.shape(SelfUserRecord.propTypes)
  }

  AppHeader.eventHandlerKeys.forEach(key => {
    AppHeader.propTypes[key] = PropTypes.func
  })
}

module.exports = AppHeader
